import React, { useEffect, useImperativeHandle, useState } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Chip, Divider, Grid, IconButton, Stack, TextField, Typography } from '@mui/material';

import DetailMetaHeader from 'components/condition-list/DetailMetaHeader';
import ReadOnlyField from 'components/form/ReadOnlyField';
import TextArea from 'components/form/TextArea';
import UserSelect from 'components/form/UserSelect';
import { MSG_MAX_LENGTH, MSG_REQUIRED } from 'constant/ValidationMessages';
import {
  AssessmentDetailResult,
  getAssessment,
  updateAssessment,
} from 'stores/AssessmentStoreAdapter';
import { UserResult } from 'stores/UserStoreAdapter';
import { formatDate } from 'utils/DateTimeFormatter';
import { errorDefault, successDefault } from 'utils/Toastify';

export type AssessmentUpdateFormHandle = {
  submit: () => void;
  isEditing: () => boolean;
};

type AssessmentUpdateFormProps = {
  assessment: AssessmentDetailResult;
  users: UserResult[];
  isEditing: boolean;
  onUpdated: (updated: AssessmentDetailResult) => void;
  onClose: () => void;
  onEditingChange: (editing: boolean) => void;
  setProgress: (v: boolean) => void;
  onDisposalClick: () => void;
  /** コンソールドロワー内で使う場合など、フォーム内蔵ヘッダーを非表示にする（既定: false） */
  hideHeader?: boolean;
};

/** DetailPanel の p:3 (24px) を打ち消す sticky ヘッダー sx */
const STICKY_HEADER_SX = {
  position: 'sticky',
  top: -24,
  zIndex: 2,
  bgcolor: 'background.paper',
  borderBottom: 1,
  borderColor: 'divider',
  pb: 1,
  mx: -3,
  px: 3,
  pt: 3,
  mt: -3,
} as const;

const AssessmentUpdateForm = React.forwardRef<
  AssessmentUpdateFormHandle,
  AssessmentUpdateFormProps
>(
  (
    {
      assessment,
      users,
      isEditing,
      onUpdated,
      onClose,
      onEditingChange,
      setProgress,
      onDisposalClick,
      hideHeader = false,
    },
    ref,
  ) => {
    const [assessmentName, setAssessmentName] = useState(assessment.assessmentName);
    const [managementNumber1, setManagementNumber1] = useState(assessment.managementNumber1 || '');
    const [managementNumber2, setManagementNumber2] = useState(assessment.managementNumber2 || '');
    const [managementNumber3, setManagementNumber3] = useState(assessment.managementNumber3 || '');
    const [userId, setUserId] = useState<string | null>(assessment.userId || null);
    const [managerId, setManagerId] = useState<string | null>(assessment.managerId || null);
    const [information1, setInformation1] = useState(assessment.information1 || '');
    const [information2, setInformation2] = useState(assessment.information2 || '');
    const [information3, setInformation3] = useState(assessment.information3 || '');
    const [note, setNote] = useState(assessment.note || '');

    const [errors, setErrors] = useState<Record<string, string>>({});

    // 削除されたユーザーの表示対応
    const hasDeletedUser =
      assessment.userId === '' || !users.find((u) => u.userId === assessment.userId);
    const hasDeletedManager =
      assessment.managerId === '' || !users.find((u) => u.userId === assessment.managerId);

    useEffect(() => {
      setAssessmentName(assessment.assessmentName);
      setManagementNumber1(assessment.managementNumber1 || '');
      setManagementNumber2(assessment.managementNumber2 || '');
      setManagementNumber3(assessment.managementNumber3 || '');
      setUserId(assessment.userId || null);
      setManagerId(assessment.managerId || null);
      setInformation1(assessment.information1 || '');
      setInformation2(assessment.information2 || '');
      setInformation3(assessment.information3 || '');
      setNote(assessment.note || '');
      setErrors({});
      onEditingChange(false);
    }, [assessment]);

    const handleBackToReadOnly = () => {
      setAssessmentName(assessment.assessmentName);
      setManagementNumber1(assessment.managementNumber1 || '');
      setManagementNumber2(assessment.managementNumber2 || '');
      setManagementNumber3(assessment.managementNumber3 || '');
      setUserId(assessment.userId || null);
      setManagerId(assessment.managerId || null);
      setInformation1(assessment.information1 || '');
      setInformation2(assessment.information2 || '');
      setInformation3(assessment.information3 || '');
      setNote(assessment.note || '');
      setErrors({});
      onEditingChange(false);
    };

    const validate = (): boolean => {
      const newErrors: Record<string, string> = {};

      if (!assessmentName) newErrors.assessmentName = MSG_REQUIRED('資産名');
      else if (assessmentName.length > 200)
        newErrors.assessmentName = MSG_MAX_LENGTH('資産名', 200);

      if (managementNumber1 && managementNumber1.length > 50)
        newErrors.managementNumber1 = MSG_MAX_LENGTH('管理番号1', 50);
      if (managementNumber2 && managementNumber2.length > 50)
        newErrors.managementNumber2 = MSG_MAX_LENGTH('管理番号2', 50);
      if (managementNumber3 && managementNumber3.length > 50)
        newErrors.managementNumber3 = MSG_MAX_LENGTH('管理番号3', 50);

      if (!userId) newErrors.userId = MSG_REQUIRED('利用者');
      if (!managerId) newErrors.managerId = MSG_REQUIRED('管理者');

      if (userId && hasDeletedUser && userId === assessment.userId)
        newErrors.userId = '利用者を再選択してください';
      if (managerId && hasDeletedManager && managerId === assessment.managerId)
        newErrors.managerId = '管理者を再選択してください';

      if (information1 && information1.length > 100)
        newErrors.information1 = MSG_MAX_LENGTH('情報1', 100);
      if (information2 && information2.length > 100)
        newErrors.information2 = MSG_MAX_LENGTH('情報2', 100);
      if (information3 && information3.length > 100)
        newErrors.information3 = MSG_MAX_LENGTH('情報3', 100);

      if (note && note.length > 10000) newErrors.note = MSG_MAX_LENGTH('備考', 10000);

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
      if (!validate()) return;

      setProgress(true);
      updateAssessment({
        assessmentId: assessment.assessmentId,
        assessmentName,
        ...(managementNumber1 && { managementNumber1 }),
        ...(managementNumber2 && { managementNumber2 }),
        ...(managementNumber3 && { managementNumber3 }),
        userId: userId!,
        managerId: managerId!,
        ...(information1 && { information1 }),
        ...(information2 && { information2 }),
        ...(information3 && { information3 }),
        ...(note && { note }),
      })
        .then(() => {
          successDefault('資産情報を更新しました');
          return getAssessment(assessment.assessmentId);
        })
        .then((detail) => {
          onUpdated(detail);
          onEditingChange(false);
        })
        .catch((e: Error) => {
          errorDefault('資産情報の更新に失敗しました', e.message);
        })
        .finally(() => setProgress(false));
    };

    useImperativeHandle(ref, () => ({ submit: handleSave, isEditing: () => isEditing }));

    // ユーザーリストに削除されたユーザーを追加
    const usersWithDeleted = React.useMemo(() => {
      const list = [...users];
      if (hasDeletedUser && assessment.userId) {
        list.push({
          userId: assessment.userId,
          userName: '削除されたユーザー',
          mailAddress: '',
          roles: [],
        } as UserResult);
      }
      if (hasDeletedManager && assessment.managerId && assessment.managerId !== assessment.userId) {
        list.push({
          userId: assessment.managerId,
          userName: '削除されたユーザー',
          mailAddress: '',
          roles: [],
        } as UserResult);
      }
      return list;
    }, [users, assessment, hasDeletedUser, hasDeletedManager]);

    /** 共通ヘッダー */
    const renderHeader = (title: string, subtitle: string, onBack: () => void) => (
      <Box sx={STICKY_HEADER_SX}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton size="small" onClick={onBack}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Typography variant="h6" fontWeight={700}>
              {title}
            </Typography>
            {assessment.isDeleted && (
              <Chip
                label="除却済"
                size="small"
                color="error"
                sx={{ height: 22, fontSize: '0.75rem', fontWeight: 600 }}
              />
            )}
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4.5 }}>
            {subtitle}
          </Typography>
          <Stack direction="column" spacing={0.25} alignItems="flex-end">
            <Typography variant="caption" color="text.secondary">
              作成者: {assessment.createdByName || '-'} 作成日: {formatDate(assessment.createdAt)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              更新者: {assessment.updatedByName || '-'} 更新日: {formatDate(assessment.updatedAt)}
            </Typography>
          </Stack>
        </Stack>
      </Box>
    );

    // --- 参照モード ---
    if (!isEditing) {
      return (
        <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {!hideHeader && renderHeader('資産詳細', '資産情報を確認します', onClose)}

          <Stack spacing={3} sx={{ flex: 1, overflow: 'auto', pt: hideHeader ? 0 : 2 }}>
            {hideHeader && (
              <DetailMetaHeader
                id={assessment.assessmentId}
                metaItems={[
                  {
                    label: '作成者',
                    value: `${assessment.createdByName || '-'} (${formatDate(assessment.createdAt)})`,
                  },
                  {
                    label: '更新者',
                    value: `${assessment.updatedByName || '-'} (${formatDate(assessment.updatedAt)})`,
                  },
                ]}
              />
            )}
            {/* 除却情報（除却済みの場合は一番上に表示） */}
            {assessment.isDeleted && (
              <>
                <Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    color="error.main"
                    sx={{ mb: 1.5 }}
                  >
                    除却情報
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={6}>
                      <ReadOnlyField
                        label="除却理由"
                        value={assessment.disposalReasonName || '-'}
                      />
                    </Grid>
                    <Grid size={6}>
                      <ReadOnlyField
                        label="除却日"
                        value={assessment.disposedAt ? formatDate(assessment.disposedAt) : '-'}
                      />
                    </Grid>
                    <Grid size={12}>
                      <ReadOnlyField label="除却詳細" value={assessment.disposalDetail || '-'} />
                    </Grid>
                  </Grid>
                </Box>
                <Divider />
              </>
            )}

            {/* 基本情報 */}
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                基本情報
              </Typography>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <ReadOnlyField label="資産区分" value={assessment.assessmentTypeName} />
                </Grid>
                <Grid size={6}>
                  <ReadOnlyField label="資産名" value={assessment.assessmentName} />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* 管理番号 */}
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                管理番号
              </Typography>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <ReadOnlyField label="管理番号1" value={assessment.managementNumber1 || '-'} />
                </Grid>
                <Grid size={12}>
                  <ReadOnlyField label="管理番号2" value={assessment.managementNumber2 || '-'} />
                </Grid>
                <Grid size={12}>
                  <ReadOnlyField label="管理番号3" value={assessment.managementNumber3 || '-'} />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* 利用者・管理者 */}
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                利用者・管理者
              </Typography>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <ReadOnlyField label="利用者" value={assessment.userName || '-'} />
                </Grid>
                <Grid size={6}>
                  <ReadOnlyField label="管理者" value={assessment.managerName || '-'} />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* 情報 */}
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                情報
              </Typography>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <ReadOnlyField label="情報1" value={assessment.information1 || '-'} />
                </Grid>
                <Grid size={12}>
                  <ReadOnlyField label="情報2" value={assessment.information2 || '-'} />
                </Grid>
                <Grid size={12}>
                  <ReadOnlyField label="情報3" value={assessment.information3 || '-'} />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* 備考 */}
            <ReadOnlyField label="備考" value={assessment.note || '-'} />
          </Stack>
        </Stack>
      );
    }

    // --- 編集モード ---
    return (
      <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {!hideHeader &&
          renderHeader('資産詳細（編集中）', '資産情報を編集します', handleBackToReadOnly)}

        <Stack spacing={3} sx={{ flex: 1, overflow: 'auto', pt: hideHeader ? 0 : 2 }}>
          {hideHeader && (
            <DetailMetaHeader
              id={assessment.assessmentId}
              metaItems={[
                {
                  label: '作成者',
                  value: `${assessment.createdByName || '-'} (${formatDate(assessment.createdAt)})`,
                },
                {
                  label: '更新者',
                  value: `${assessment.updatedByName || '-'} (${formatDate(assessment.updatedAt)})`,
                },
              ]}
            />
          )}
          {/* 基本情報 */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              基本情報
            </Typography>
            <Grid container spacing={2}>
              <Grid size={12}>
                <ReadOnlyField label="資産区分" value={assessment.assessmentTypeName} />
              </Grid>
              <Grid size={12}>
                <TextField
                  label="資産名"
                  value={assessmentName}
                  onChange={(e) => setAssessmentName(e.target.value)}
                  fullWidth
                  size="small"
                  required
                  error={!!errors.assessmentName}
                  helperText={errors.assessmentName}
                  slotProps={{ htmlInput: { maxLength: 200 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* 管理番号 */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              管理番号
            </Typography>
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  label="管理番号1"
                  value={managementNumber1}
                  onChange={(e) => setManagementNumber1(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!errors.managementNumber1}
                  helperText={errors.managementNumber1}
                  slotProps={{ htmlInput: { maxLength: 50 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  label="管理番号2"
                  value={managementNumber2}
                  onChange={(e) => setManagementNumber2(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!errors.managementNumber2}
                  helperText={errors.managementNumber2}
                  slotProps={{ htmlInput: { maxLength: 50 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  label="管理番号3"
                  value={managementNumber3}
                  onChange={(e) => setManagementNumber3(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!errors.managementNumber3}
                  helperText={errors.managementNumber3}
                  slotProps={{ htmlInput: { maxLength: 50 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* 利用者・管理者 */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              利用者・管理者
            </Typography>
            <Grid container spacing={2}>
              <Grid size={6}>
                <UserSelect
                  mode="single"
                  users={usersWithDeleted}
                  selectedUserId={userId}
                  onChange={setUserId}
                  label="利用者"
                  required
                  error={!!errors.userId}
                  helperText={errors.userId}
                />
              </Grid>
              <Grid size={6}>
                <UserSelect
                  mode="single"
                  users={usersWithDeleted}
                  selectedUserId={managerId}
                  onChange={setManagerId}
                  label="管理者"
                  required
                  error={!!errors.managerId}
                  helperText={errors.managerId}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* 情報 */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              情報
            </Typography>
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  label="情報1"
                  value={information1}
                  onChange={(e) => setInformation1(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!errors.information1}
                  helperText={errors.information1}
                  slotProps={{ htmlInput: { maxLength: 100 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  label="情報2"
                  value={information2}
                  onChange={(e) => setInformation2(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!errors.information2}
                  helperText={errors.information2}
                  slotProps={{ htmlInput: { maxLength: 100 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  label="情報3"
                  value={information3}
                  onChange={(e) => setInformation3(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!errors.information3}
                  helperText={errors.information3}
                  slotProps={{ htmlInput: { maxLength: 100 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* 備考 */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              備考
            </Typography>
            <TextArea
              label="備考"
              value={note}
              onChange={setNote}
              maxLength={10000}
              rows={6}
              fullWidth
              errorMessage={errors.note}
            />
          </Box>
        </Stack>
      </Stack>
    );
  },
);

AssessmentUpdateForm.displayName = 'AssessmentUpdateForm';

export default AssessmentUpdateForm;
